import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  Chip,
  Alert,
  CircularProgress,
  Divider,
  IconButton,
  Avatar
} from '@mui/material';
import {
  Email as EmailIcon,
  WhatsApp as WhatsAppIcon,
  Phone as PhoneIcon,
  CheckCircle as CheckIcon,
  Cancel as CancelIcon,
  Refresh as RefreshIcon,
  Close as CloseIcon,
  Extension as ExtensionIcon
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { createIntegration, getIntegrations, getIntegrationTools } from '../../../../store/Integration';
import ConnectionStatus from '../../../../components/Integration/ConnectionStatus';
import { fontSize, height, textTransform, width } from '../../../../../node_modules/@mui/system/index';

const WorkflowConnectionModal = ({ 
  open, 
  onClose, 
  requiredConnections, 
  onConnectionsComplete,
  workflowName 
}) => {
  const dispatch = useDispatch();
  const [refreshing, setRefreshing] = useState(false);
  const [connectingService, setConnectingService] = useState(null);
  
  // Redux state
  const { integrations, tools } = useSelector(state => state.Integration);
  
  // Get tool metadata for display
  const toolsData = useMemo(() => {
    if (!tools?.data || !Array.isArray(tools.data)) return {};
    
    // Create a map of tool names (lowercase) to tool data
    const toolMap = {};
    tools.data.forEach(tool => {
      if (tool.name) {
        const key = tool.name.toLowerCase().replace(/\s+/g, '');
        toolMap[key] = tool;
      }
    });
    return toolMap;
  }, [tools]);
  
  // Helper function to extract display data from integration
  const getServiceDisplayData = (serviceName, integration) => {
    const data = {};
    
    // Common fields that might contain display data
    if (integration.email) data.email = integration.email;
    if (integration.phoneNumber) data.phoneNumber = integration.phoneNumber;
    if (integration.authData?.email) data.email = integration.authData.email;
    if (integration.authData?.phoneNumber) data.phoneNumber = integration.authData.phoneNumber;
    if (integration.workspace) data.workspace = integration.workspace;
    
    // Default display text if no specific data
    if (Object.keys(data).length === 0) {
      data.displayText = 'Connected';
    }
    
    return data;
  };
  
  // Merge fresh integration data with required connections
  const updatedConnections = useMemo(() => {
    if (!requiredConnections || !integrations) return requiredConnections;
    
    const updated = { ...requiredConnections };
    
    // Dynamically update connection status for all required connections
    Object.keys(updated).forEach(serviceName => {
      if (updated[serviceName]) {
        // Find matching integration for this service
        const matchingIntegration = integrations.find(int => {
          // Check multiple possible field structures
          const serviceNameLower = serviceName.toLowerCase();
          const isMatchingService = 
            (int.toolName && int.toolName.toLowerCase().includes(serviceNameLower)) ||
            (int.name && int.name.toLowerCase().includes(serviceNameLower)) ||
            (int.tool && int.tool.name && int.tool.name.toLowerCase().includes(serviceNameLower));
          
          const isConnected = int.isConnected || int.connected || int.status === 'connected';
          
          return isMatchingService && isConnected;
        });
        
        if (matchingIntegration) {
          updated[serviceName] = {
            ...updated[serviceName],
            connected: true,
            // Get display data based on service type
            ...(getServiceDisplayData(serviceName, matchingIntegration))
          };
        }
      }
    });
    
    return updated;
  }, [requiredConnections, integrations]);
  
  // Check if all required connections are satisfied using updated data
  const allConnected = updatedConnections && Object.values(updatedConnections)
    .every(conn => !conn.required || conn.connected);
    
  useEffect(() => {
    if (open) {
      // Refresh integration status when modal opens
      dispatch(getIntegrations());
      dispatch(getIntegrationTools());
    }
  }, [open, dispatch]);
  
  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await dispatch(getIntegrations());
    } finally {
      setRefreshing(false);
    }
  };
  
  const handleConnect = (service) => {
    setConnectingService(service);
    // The individual connection components handle the actual connection
  };
  
  const handleProceed = () => {
    onConnectionsComplete();
  };
  
  // Get service icon dynamically
  const getServiceIcon = (service) => {
    const serviceKey = service.toLowerCase().replace(/\s+/g, '');
    const toolData = toolsData[serviceKey];
    
    // If we have tool data with logo or icon
    if (toolData) {
      if (toolData.logo) {
        return <Avatar src={toolData.logo} sx={{ width: 24, height: 24 }} />;
      }
      if (toolData.iconSvg) {
        return (
          <Box 
            dangerouslySetInnerHTML={{ __html: toolData.iconSvg }} 
            sx={{ width: 24, height: 24, display: 'flex', alignItems: 'center' }}
          />
        );
      }
    }
    
    // Fallback to default icons
    switch (service.toLowerCase()) {
      case 'gmail':
        return <EmailIcon />;
      case 'whatsapp':
        return <WhatsAppIcon />;
      case 'twilio':
      case 'twilio sms':
      case 'twilio voice':
        return <PhoneIcon />;
      default:
        return <ExtensionIcon />;
    }
  };
  
  // Get service display name dynamically
  const getServiceName = (service) => {
    const serviceKey = service.toLowerCase().replace(/\s+/g, '');
    const toolData = toolsData[serviceKey];
    
    // Use tool name from database if available
    if (toolData?.name) {
      return toolData.name;
    }
    
    // Fallback to formatted service name
    return service.charAt(0).toUpperCase() + service.slice(1);
  };
  
  // Get service description dynamically
  const getServiceDescription = (service, connectionData) => {
    const serviceKey = service.toLowerCase().replace(/\s+/g, '');
    const toolData = toolsData[serviceKey];
    
    // If connected, show connection details
    if (connectionData?.connected) {
      if (connectionData.email) return `Connected: ${connectionData.email}`;
      if (connectionData.phoneNumber) return `Connected: ${connectionData.phoneNumber}`;
      if (connectionData.workspace) return `Connected: ${connectionData.workspace}`;
      return 'Connected';
    }
    
    // Use tool description from database if available
    if (toolData?.description) {
      return toolData.description;
    }
    
    // Fallback descriptions
    return `Required for ${getServiceName(service)} functionality`;
  };
  
  if (!updatedConnections) return null;
  
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown={!allConnected}
      sx={{
        '& .MuiDialog-paper': {
          margin: '16px',
          width: 'calc(100% - 32px)',
          maxHeight: 'calc(100% - 32px)',
        },
        '& .MuiDialogContent-root': {
          padding: {
            xs: '16px', // Smaller padding on mobile
            sm: '24px'  // Default padding on larger screens
          }
        },
        '& .MuiListItem-root': {
          flexDirection: {
            xs: 'column',  // Stack vertically on mobile
            sm: 'row'      // Original horizontal layout on larger screens
          },
          alignItems: {
            xs: 'flex-start',
            sm: 'center'
          },
          gap: '8px',
          padding: {
            xs: '12px 8px',
            sm: '16px'
          }
        },
        '& .MuiListItemSecondaryAction-root': {
          position: {
            xs: 'relative',  // Normal position in flow on mobile
            sm: 'absolute'   // Original absolute position on larger screens
          },
          transform: {
            xs: 'none',
            sm: 'translateY(-50%)'
          },
          right: {
            xs: 'auto',
            sm: '16px'
          },
          marginTop: {
            xs: '8px',
            sm: 0
          }
        },
        '& .MuiListItemText-root': {
          marginRight: {
            xs: '0',
            sm: '48px'
          }
        },
        '& .MuiDialogActions-root': {
          padding: {
            xs: '8px 16px',
            sm: '16px 24px'
          },
          flexDirection: {
            xs: 'column',    // Stack buttons on mobile
            sm: 'row'        // Original horizontal layout
          },
          '& button': {
            width: {
              xs: '100%',    // Full width buttons on mobile
              sm: 'auto'     // Original width
            },
            margin: {
              xs: '4px 0',   // Vertical spacing between stacked buttons
              sm: '0 8px'    // Original horizontal spacing
            }
          }
        }
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" sx={{textTransform: 'capitalize', fontSize: '16px'}}>Connect Required Services</Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <IconButton size="small" onClick={handleRefresh} disabled={refreshing}>
              {refreshing ? <CircularProgress size={20} /> : <RefreshIcon />}
            </IconButton>
            {allConnected && (
              <IconButton size="small" onClick={onClose}>
                <CloseIcon />
              </IconButton>
            )}
          </Box>
        </Box>
      </DialogTitle>
      
      <DialogContent>
        <Box>
          {workflowName && (
            <Alert severity="info" sx={{ mb: 3 }}>
              The template "{workflowName}" requires the following services to be connected before you can use it.
            </Alert>
          )}

          <h5>Required Connections:</h5>
          
          <List>
            {/* Dynamically render all required connections */}
            {Object.entries(updatedConnections).map(([serviceName, connectionData]) => {
              if (!connectionData?.required) return null;

              return (
                <React.Fragment key={serviceName}>
                  <ListItem>
                    <ListItemIcon>
                      {getServiceIcon(serviceName)}
                    </ListItemIcon>
                    <ListItemText
                      primary={getServiceName(serviceName)}
                      secondary={getServiceDescription(serviceName, connectionData)}
                    />
                    <ListItemSecondaryAction>
                      <Chip
                        icon={connectionData.connected ? <CheckIcon /> : <CancelIcon />}
                        label={connectionData.connected ? 'Connected' : 'Not Connected'}
                        color={connectionData.connected ? 'success' : 'error'}
                        size="small"
                      />
                    </ListItemSecondaryAction>
                  </ListItem>
                  {!connectionData.connected && (
                    <Box sx={{ px: 2, pb: 2 }}>
                      <ConnectionStatus
                        toolName={serviceName}
                        compact={true}
                        onConnect={() => handleConnect(serviceName)}
                      />
                    </Box>
                  )}
                  <Divider variant="inset" component="li" />
                </React.Fragment>
              );
            })}
          </List>
          
          {/* Summary */}
          {allConnected && (
            <Alert severity="success" sx={{ mt: 2 }}>
              All required services are connected! You can now proceed to use the template.
            </Alert>
          )}
        </Box>
      </DialogContent>
      
      <DialogActions>
        <button
          className="btn-style-border"
          onClick={onClose}
        >
          Cancel
        </button>
        <button
          variant="contained"
          className="btn-style-new"
          onClick={handleProceed}
          disabled={!allConnected}
        >
          <i className="icon"><CheckIcon sx={{width: '15px', height: '15px'}}/></i>
          Proceed
        </button>
      </DialogActions>
    </Dialog>
  );
};

export default WorkflowConnectionModal;